import React, { useState, useCallback, useMemo, useEffect } from 'react';
import PropTypes from 'prop-types';
import _ from 'underscore';
import classNames from 'classnames';
import { makeStyles } from '@fluentui/react-components';
import { getUrlProtocolAndDomain, isValidUrl } from '@bingads-webui/url-util';
import { DomainInputView } from './domain-input-view';
import { SubdomainInputView } from './subdomain-input-view';
import { useWebsiteImages } from './useWebsiteImages';
import { PanelImageGrid } from '../panel-image-grid';
import { AdsStudioSpinner } from '../ads-studio-spinner';

const subdomainUrlRegex = /^https?:\/\/([a-z0-9-_]+\.)*[a-z0-9-_]+\.[a-z]{2,63}(?=$|\/)\//i;

const useStyles = makeStyles({
  contentWrapper: {
    paddingTop: '8px',
  },
});

const getSubdomain = (url) => {
  const regexResult = url.trim().split(subdomainUrlRegex);
  if (regexResult && regexResult.length > 1) {
    return regexResult[2];
  }
  return url;
};

export const WebsiteImagePanelContent = (props) => {
  const {
    aid, cid, oDataConfig, i18n, setSelectedImages, overlayBodyRef,
  } = props;
  const [domainUrl, setDomailUrl] = useState('');
  const [subdomain, setSubdomain] = useState('');
  const [isDomainInvalid, setIsdomainInvalid] = useState(false);
  const classes = useStyles();

  const {
    setPageNumber,
    loadNextPage,
    loadImages,
    images,
    totalCount,
    isLoadingFistPage,
    isLoading,
    setImages,
    onSelectImage,
  } = useWebsiteImages({
    aid, cid, oDataConfig, setSelectedImages,
  });

  const finalUrl = useMemo(() => `${domainUrl}/${subdomain}`, [domainUrl, subdomain]);
  const showDomain = useMemo(() => !finalUrl || !isValidUrl(finalUrl), [finalUrl]);

  const finalUrlDomain = useMemo(() => {
    if (finalUrl) {
      return getUrlProtocolAndDomain(finalUrl);
    }
    return '';
  }, [finalUrl]);

  const onDomainChange = useCallback((newDomainUrl) => {
    setDomailUrl(newDomainUrl);
    if (isValidUrl(newDomainUrl)) {
      setIsdomainInvalid(false);
      setPageNumber(1);
      setImages([]);
      loadImages({ url: `${newDomainUrl}/${subdomain}`, sDomain: subdomain });
    } else {
      setIsdomainInvalid(true);
    }
  }, [loadImages, setImages, setPageNumber, subdomain]);

  const onSubdomainChange = useCallback((newSubdomainUrl) => {
    if (!_.isNull(newSubdomainUrl) && !_.isUndefined(newSubdomainUrl)) {
      const newValue = getSubdomain(newSubdomainUrl);
      const newSearchUrl = `${finalUrlDomain}/${newValue}`;
      if (newSearchUrl !== finalUrl) {
        setSubdomain(newValue);
        setPageNumber(1);
        setImages([]);
        loadImages({ url: newSearchUrl, sDomain: newValue });
      }
    }
  }, [finalUrl, finalUrlDomain, loadImages, setImages, setPageNumber]);

  const loadMoreData = useCallback(() => {
    loadNextPage({ url: finalUrl, sDomain: subdomain });
  }, [finalUrl, loadNextPage, subdomain]);

  useEffect(() => {
    if (showDomain) {
      setDomailUrl('');
      setSubdomain('');
    }
  }, [showDomain]);

  return (
    <div className={classNames('website-content-container', classes.contentWrapper)}>
      {
        showDomain ?
          <DomainInputView isDomainInvalid={isDomainInvalid} i18n={i18n} onDomainChange={onDomainChange} /> :
          <SubdomainInputView defaultDomainUrl={domainUrl} i18n={i18n} isLoading={isLoading} onDomainUrlChange={onDomainChange} onSubdomainUrlChange={onSubdomainChange} />
      }
      {
        !showDomain && !isLoading && images.length === 0 &&
        <div className="alert alert-info" style={{ marginTop: '20px' }}>
          <div className="alert-image" />
          <div className="alert-info-inline">
            {i18n.getString(_TL_('No suggestion found from your site.'))}
          </div>
        </div>
      }
      {
        !showDomain && (
          <>
            { isLoadingFistPage && !images.length && <AdsStudioSpinner useCustomSize customStyle={{ marginTop: '50px' }} /> }
            {!!images.length && (
              <PanelImageGrid
                i18n={i18n}
                onSelectImage={onSelectImage}
                overlayBodyRef={overlayBodyRef}
                isLoadingMore={isLoading}
                dataService={{
                  assets: images,
                  totalAssetCount: totalCount,
                  loadMoreData,
                }}
              />
            )}
          </>
        )
      }
    </div>
  );
};

WebsiteImagePanelContent.prototype = {
  aid: PropTypes.number.isRequired,
  cid: PropTypes.number.isRequired,
  oDataConfig: PropTypes.shape({}).isRequired,
  i18n: PropTypes.shape({
    getString: PropTypes.func,
  }).isRequired,
  setSelectedImages: PropTypes.func.isRequired,
};
